// src/components/manager-v2/TeamPerformance.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { TrendingUp, Award, Target, Star, BarChart3, Loader, AlertCircle, UserPlus, Edit, LineChart, Eye, Info, X, Search, Filter, Download } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import performanceService from '../../services/performance.service';
import PerformanceReviewModal from './PerformanceReviewModal';
import TeamPerformanceAnalytics from './TeamPerformanceAnalytics';

const TeamPerformance = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('performance'); // 'performance', 'self-reviews', or 'analytics'
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);
    const [performanceData, setPerformanceData] = useState([]);
    const [selfReviews, setSelfReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedSelfReview, setSelectedSelfReview] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewMode, setReviewMode] = useState('new'); // 'new' or 'edit'
    const [showActionMenu, setShowActionMenu] = useState(null);
    const [showSelfReviewDetails, setShowSelfReviewDetails] = useState(false);
    const [showProductivityInfoPerformance, setShowProductivityInfoPerformance] = useState(false);
    const [showProductivityInfoSelfReviews, setShowProductivityInfoSelfReviews] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [performanceFilter, setPerformanceFilter] = useState('all'); // 'all', 'excellent', 'good', 'needs_improvement'

    // Filtered performance data
    const filteredPerformanceData = useMemo(() => {
        return performanceData.filter(employee => {
            // Search filter
            const matchesSearch = searchQuery === '' ||
                `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                employee.designation?.toLowerCase().includes(searchQuery.toLowerCase());

            // Performance filter
            const rating = employee.rating ? parseFloat(employee.rating) : 0;
            const matchesPerformance = performanceFilter === 'all' ||
                (performanceFilter === 'excellent' && rating >= 4.5) ||
                (performanceFilter === 'good' && rating >= 3.5 && rating < 4.5) ||
                (performanceFilter === 'needs_improvement' && rating > 0 && rating < 3.5);

            return matchesSearch && matchesPerformance;
        });
    }, [performanceData, searchQuery, performanceFilter]);

    // Fetch team performance data
    const fetchTeamPerformance = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await performanceService.getTeamPerformance();
            setPerformanceData(response.performance || []);
        } catch (err) {
            console.error('Error fetching team performance:', err);
            setError('Failed to load team performance data');
            setPerformanceData([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch team's self-reviews
    const fetchTeamSelfReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const reviews = await performanceService.getTeamSelfReviews();
            setSelfReviews(reviews);
        } catch (err) {
            console.error('Error fetching team self-reviews:', err);
            setError('Failed to load team self-reviews');
            setSelfReviews([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'performance') {
            fetchTeamPerformance();
        } else if (activeTab === 'self-reviews') {
            fetchTeamSelfReviews();
        }
    }, [activeTab]);

    // Handle employee row click
    const handleEmployeeClick = (employee, e) => {
        // Don't trigger if clicking on a button or link
        if (e.target.closest('button')) return;
        setShowActionMenu(showActionMenu === employee.id ? null : employee.id);
    };

    const handleNewReview = (employee) => {
        setSelectedEmployee(employee);
        setReviewMode('new');
        setShowReviewModal(true);
        setShowActionMenu(null);
    };

    const handleEditReview = (employee) => {
        setSelectedEmployee(employee);
        setReviewMode('edit');
        setShowReviewModal(true);
        setShowActionMenu(null);
    };

    const handleCloseModal = () => {
        setShowReviewModal(false);
        setSelectedEmployee(null);
        fetchTeamPerformance(); // Refresh data after modal closes
    };

    // Export performance data to CSV
    const handleExportToCSV = () => {
        const headers = ['Employee Name', 'Designation', 'Rating', 'Active Goals', 'Completed Goals', 'Goals Progress %', 'Productivity %', 'Last Review Date', 'Status'];

        const csvData = filteredPerformanceData.map(employee => {
            const rating = employee.rating ? parseFloat(employee.rating) : 0;
            const activeGoals = employee.active_goals || 0;
            const completedGoals = employee.completed_goals || 0;
            const totalGoals = activeGoals + completedGoals;
            const goalsProgress = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;
            const productivity = employee.productivity || 0;
            const lastReviewDate = employee.completed_at ? new Date(employee.completed_at).toLocaleDateString() : 'No review yet';
            const status = rating >= 4.5 ? 'Excellent' : rating >= 3.5 ? 'Good' : rating > 0 ? 'Needs Improvement' : 'Not Rated';

            return [
                `${employee.first_name} ${employee.last_name}`,
                employee.designation || 'N/A',
                rating > 0 ? rating.toFixed(1) : 'N/A',
                activeGoals,
                completedGoals,
                goalsProgress,
                productivity,
                lastReviewDate,
                status
            ].map(field => `"${field}"`).join(',');
        });

        const csvContent = [headers.join(','), ...csvData].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `team_performance_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Calculate team stats from real data
    const calculateTeamStats = () => {
        if (performanceData.length === 0) {
            return {
                avgRating: '0.0',
                avgProductivity: 0,
                topPerformers: 0,
                needsAttention: 0
            };
        }

        const validRatings = performanceData.filter(emp => emp.rating !== null);
        const avgRating = validRatings.length > 0
            ? (validRatings.reduce((sum, emp) => sum + parseFloat(emp.rating), 0) / validRatings.length).toFixed(1)
            : '0.0';

        const avgProductivity = performanceData.length > 0
            ? Math.floor(performanceData.reduce((sum, emp) => sum + (emp.productivity || 0), 0) / performanceData.length)
            : 0;

        const topPerformers = performanceData.filter(emp => emp.rating && parseFloat(emp.rating) >= 4.5).length;
        const needsAttention = performanceData.filter(emp => emp.rating && parseFloat(emp.rating) < 3.5).length;

        return { avgRating, avgProductivity, topPerformers, needsAttention };
    };

    const teamStats = calculateTeamStats();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Performance</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Monitor and evaluate your team's performance metrics
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('performance')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'performance'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5" />
                            Team Performance
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('self-reviews')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'self-reviews'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Star className="w-5 h-5" />
                            Team Self Reviews
                            {selfReviews.length > 0 && (
                                <span className="ml-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold px-2 py-0.5 rounded-full">
                                    {selfReviews.length}
                                </span>
                            )}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'analytics'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <LineChart className="w-5 h-5" />
                            Team Analytics
                        </div>
                    </button>
                </nav>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
                </div>
            )}

            {/* Team Performance Tab Content */}
            {activeTab === 'performance' && !loading && (
            <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Average Rating</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats.avgRating}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Out of 5.0</p>
                        </div>
                        <Star className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Avg Productivity</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{teamStats.avgProductivity}%</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Top Performers</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{teamStats.topPerformers}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Rating ≥ 4.5</p>
                        </div>
                        <Award className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Needs Attention</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{teamStats.needsAttention}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Rating &lt; 3.5</p>
                        </div>
                        <Target className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                    </div>
                </div>
            </div>
            )}

            {/* Performance Table */}
            {!loading && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team Performance Overview</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExportToCSV}
                            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                            title="Export to CSV"
                        >
                            <Download className="w-4 h-4" />
                            Export CSV
                        </button>
                        <div className="relative">
                            <button
                                onClick={() => setShowProductivityInfoPerformance(!showProductivityInfoPerformance)}
                                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                title="Productivity Calculation Info"
                            >
                                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </button>

                            {showProductivityInfoPerformance && (
                            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 z-50">
                                <div className="flex items-start justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        Productivity Calculation
                                    </h4>
                                    <button
                                        onClick={() => setShowProductivityInfoPerformance(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="space-y-3 text-xs text-gray-600 dark:text-gray-400">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                                        <p className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Formula:</p>
                                        <p className="font-mono text-blue-800 dark:text-blue-400">
                                            Productivity = (Rating ÷ 5 × 60%) + (Goal Completion × 40%)
                                        </p>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900 dark:text-white mb-1">Components:</p>
                                        <ul className="space-y-1 ml-4 list-disc">
                                            <li><span className="font-medium">Performance Rating (60%)</span>: Based on review ratings from 0-5.0</li>
                                            <li><span className="font-medium">Goal Completion (40%)</span>: Percentage of completed goals</li>
                                        </ul>
                                    </div>
                                    <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                                        <p className="font-semibold text-gray-900 dark:text-white mb-1">Example:</p>
                                        <p>If an employee has a rating of 4.5/5.0 and 80% goal completion:</p>
                                        <p className="font-mono text-gray-800 dark:text-gray-300 mt-1">
                                            (4.5 ÷ 5 × 60) + (80 ÷ 100 × 40) = 54 + 32 = 86%
                                        </p>
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Search and Filter Controls */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search Box */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name or designation..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Performance Filter */}
                        <div className="sm:w-64 relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                            <select
                                value={performanceFilter}
                                onChange={(e) => setPerformanceFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                            >
                                <option value="all">All Performance</option>
                                <option value="excellent">Excellent (4.5+)</option>
                                <option value="good">Good (3.5-4.5)</option>
                                <option value="needs_improvement">Needs Improvement (&lt;3.5)</option>
                            </select>
                        </div>
                    </div>

                    {/* Results count */}
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredPerformanceData.length} of {performanceData.length} team members
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Employee</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Rating</th>
                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Goals Progress</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Productivity</th>
                                <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Last Review</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredPerformanceData.map((employee) => {
                                const rating = employee.rating ? parseFloat(employee.rating) : 0;
                                const activeGoals = employee.active_goals || 0;
                                const completedGoals = employee.completed_goals || 0;
                                const totalGoals = activeGoals + completedGoals;
                                const goalsProgress = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;
                                const productivity = employee.productivity || 0;
                                const lastReviewDate = employee.completed_at
                                    ? new Date(employee.completed_at).toLocaleDateString()
                                    : 'No review yet';

                                return (
                                    <tr
                                        key={employee.id}
                                        onClick={(e) => handleEmployeeClick(employee, e)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors relative"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {employee.first_name} {employee.last_name}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">{employee.designation || 'N/A'}</p>
                                                    {/* Show reporting manager for indirect reportees */}
                                                    {employee.level > 1 && employee.manager_first_name && (
                                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                            Reports to: {employee.manager_first_name} {employee.manager_last_name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Star className={`w-5 h-5 mr-1 ${rating >= 4 ? 'text-yellow-400' : 'text-gray-400'}`} fill="currentColor" />
                                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {rating > 0 ? rating.toFixed(1) : 'N/A'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {completedGoals}/{totalGoals}
                                                    </span>
                                                    <span className="text-sm text-gray-500 dark:text-gray-400">{goalsProgress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                    <div
                                                        className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full"
                                                        style={{ width: `${goalsProgress}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{productivity}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full ${
                                                            productivity >= 80 ? 'bg-green-600 dark:bg-green-500' :
                                                            productivity >= 60 ? 'bg-yellow-600 dark:bg-yellow-500' :
                                                            'bg-red-600 dark:bg-red-500'
                                                        }`}
                                                        style={{ width: `${productivity}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                            {lastReviewDate}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                rating >= 4.5
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                    : rating >= 3.5
                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                    : rating > 0
                                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                            }`}>
                                                {rating >= 4.5 ? 'Excellent' : rating >= 3.5 ? 'Good' : rating > 0 ? 'Needs Improvement' : 'Not Rated'}
                                            </span>

                                            {/* Action Menu */}
                                            {showActionMenu === employee.id && (
                                                <div className="absolute right-6 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-2 min-w-[200px]">
                                                    <button
                                                        onClick={() => handleNewReview(employee)}
                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                    >
                                                        <UserPlus className="w-4 h-4" />
                                                        Provide New Review
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditReview(employee)}
                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                        Edit Previous Review
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* Empty State for Performance Tab */}
            {performanceData.length === 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <BarChart3 className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Team Members</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                        You don't have any team members to track performance for.
                    </p>
                </div>
            )}
            </>
            )}

            {/* Team Self Reviews Tab Content */}
            {activeTab === 'self-reviews' && !loading && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team's Self Reviews</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                View and respond to your team's self-review submissions
                            </p>
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => setShowProductivityInfoSelfReviews(!showProductivityInfoSelfReviews)}
                                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                title="Productivity Calculation Info"
                            >
                                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </button>

                            {showProductivityInfoSelfReviews && (
                                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 z-50">
                                    <div className="flex items-start justify-between mb-3">
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            Productivity Calculation
                                        </h4>
                                        <button
                                            onClick={() => setShowProductivityInfoSelfReviews(false)}
                                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="space-y-3 text-xs text-gray-600 dark:text-gray-400">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                                            <p className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Formula:</p>
                                            <p className="font-mono text-blue-800 dark:text-blue-400">
                                                Productivity = (Rating ÷ 5 × 60%) + (Goal Completion × 40%)
                                            </p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900 dark:text-white mb-1">Components:</p>
                                            <ul className="space-y-1 ml-4 list-disc">
                                                <li><span className="font-medium">Performance Rating (60%)</span>: Based on review ratings from 0-5.0</li>
                                                <li><span className="font-medium">Goal Completion (40%)</span>: Percentage of completed goals</li>
                                            </ul>
                                        </div>
                                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                                            <p className="font-semibold text-gray-900 dark:text-white mb-1">Example:</p>
                                            <p>If an employee has a rating of 4.5/5.0 and 80% goal completion:</p>
                                            <p className="font-mono text-gray-800 dark:text-gray-300 mt-1">
                                                (4.5 ÷ 5 × 60) + (80 ÷ 100 × 40) = 54 + 32 = 86%
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {selfReviews.length === 0 ? (
                        <div className="p-12 text-center">
                            <Star className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Self Reviews</h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Your team members haven't submitted any self-reviews yet.
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {selfReviews.map((review) => (
                                <div key={review.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                                    {review.first_name?.[0]}{review.last_name?.[0]}
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        {review.first_name} {review.last_name}
                                                    </h3>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {review.designation}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                                                <div>
                                                    <span className="text-gray-500 dark:text-gray-400">Cycle:</span>
                                                    <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
                                                        {review.review_cycle}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500 dark:text-gray-400">Period:</span>
                                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                                        {new Date(review.review_period_start).toLocaleDateString()} - {new Date(review.review_period_end).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <div className="flex items-center">
                                                    <Star className="w-4 h-4 text-yellow-400 mr-1" />
                                                    <span className="text-gray-500 dark:text-gray-400">Rating:</span>
                                                    <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                                                        {parseFloat(review.overall_rating).toFixed(1)}/5.0
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500 dark:text-gray-400">Submitted:</span>
                                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                                        {new Date(review.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>

                                            {review.strengths && (
                                                <div className="mb-2">
                                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Strengths:</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{review.strengths}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="ml-4 flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedSelfReview(review);
                                                    setShowSelfReviewDetails(true);
                                                }}
                                                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-2"
                                            >
                                                <Eye className="w-4 h-4" />
                                                View Details
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedEmployee({
                                                        id: review.employee_id,
                                                        first_name: review.first_name,
                                                        last_name: review.last_name,
                                                        designation: review.designation
                                                    });
                                                    setSelectedSelfReview(review);
                                                    setShowReviewModal(true);
                                                }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
                                            >
                                                <Edit className="w-4 h-4" />
                                                Create Manager Review
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Team Analytics Tab Content */}
            {activeTab === 'analytics' && (
                <TeamPerformanceAnalytics />
            )}

            {/* Self-Review Details Modal */}
            {showSelfReviewDetails && selectedSelfReview && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Self-Review Details
                                </h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    {selectedSelfReview.first_name} {selectedSelfReview.last_name} - {selectedSelfReview.designation}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowSelfReviewDetails(false);
                                    setSelectedSelfReview(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            {/* Review Info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Review Cycle</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{selectedSelfReview.review_cycle}</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Period</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {new Date(selectedSelfReview.review_period_start).toLocaleDateString()} - {new Date(selectedSelfReview.review_period_end).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Overall Rating</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{parseFloat(selectedSelfReview.overall_rating).toFixed(1)}/5.0</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Submitted On</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {new Date(selectedSelfReview.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>

                            {/* Skill Ratings */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skill Ratings</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {[
                                        { label: 'Technical Skills', value: selectedSelfReview.technical_skills || 0 },
                                        { label: 'Communication Skills', value: selectedSelfReview.communication_skills || 0 },
                                        { label: 'Teamwork', value: selectedSelfReview.teamwork || 0 },
                                        { label: 'Problem Solving', value: selectedSelfReview.problem_solving || 0 },
                                        { label: 'Leadership', value: selectedSelfReview.leadership || 0 }
                                    ].map((skill, idx) => (
                                        <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{skill.label}</span>
                                                <span className="text-sm font-semibold text-gray-900 dark:text-white">{skill.value}/5.0</span>
                                            </div>
                                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-blue-600 rounded-full transition-all"
                                                    style={{ width: `${(skill.value / 5) * 100}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Text Fields */}
                            {selectedSelfReview.strengths && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Strengths</h3>
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSelfReview.strengths}</p>
                                    </div>
                                </div>
                            )}

                            {selectedSelfReview.areas_for_improvement && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Areas for Improvement</h3>
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSelfReview.areas_for_improvement}</p>
                                    </div>
                                </div>
                            )}

                            {selectedSelfReview.goals_for_next_period && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Goals for Next Period</h3>
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSelfReview.goals_for_next_period}</p>
                                    </div>
                                </div>
                            )}

                            {selectedSelfReview.additional_comments && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Additional Comments</h3>
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSelfReview.additional_comments}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowSelfReviewDetails(false);
                                    setSelectedEmployee({
                                        id: selectedSelfReview.employee_id,
                                        first_name: selectedSelfReview.first_name,
                                        last_name: selectedSelfReview.last_name,
                                        designation: selectedSelfReview.designation
                                    });
                                    setShowReviewModal(true);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <Edit className="w-4 h-4" />
                                Create Manager Review
                            </button>
                            <button
                                onClick={() => {
                                    setShowSelfReviewDetails(false);
                                    setSelectedSelfReview(null);
                                }}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Performance Review Modal */}
            <PerformanceReviewModal
                isOpen={showReviewModal}
                onClose={handleCloseModal}
                employee={selectedEmployee}
                mode={reviewMode}
                selfReviewData={selectedSelfReview}
            />
        </div>
    );
};

export default TeamPerformance;
